import { inject, Injectable } from '@angular/core';
import { Actions, createEffect } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, combineLatest, distinctUntilChanged, map, of, switchMap } from 'rxjs';
import { selectSeSearchSelectedItems } from '../root-store/root.selectors';
import { SearchFormType } from '../search-page/search-page-type.enum';
import { ImagingService } from '../services/imaging/imaging.service';
import { Logger } from '@se/se.platform.base';
import { ISpecialtyExamination, IVisitExaminationDTO } from '@se/models';

@Injectable({
    providedIn: 'root',
})
export class ImagingSafetyEffects {
    private imagingService = inject(ImagingService);
    private logger = inject(Logger);
    private store = inject(Store);

    monitorPatientSelection$ = createEffect(
        () =>
            combineLatest([
                this.store.select(selectSeSearchSelectedItems(SearchFormType.VISIT, 'id')),
                this.store.select(selectSeSearchSelectedItems(SearchFormType.SPECIALTY, 'id')),
            ]).pipe(
                map(([visitItems, specialtyItems]: [IVisitExaminationDTO[], ISpecialtyExamination[]]) => {
                    return [
                        ...visitItems.map((item) => item.patient.id),
                        ...specialtyItems.map((item) => item.patient.id),
                    ].filter(Boolean);
                }),
                distinctUntilChanged((prev: string[], curr: string[]) => {
                    if (prev.length !== curr.length) return false;
                    return prev.every((id, index) => id === curr[index]);
                }),
                switchMap(async (selectedIds: string[]) => {
                    const imagesOpen = await this.imagingService.areImagesOpen();
                    if (imagesOpen && selectedIds.length > 0) {
                        this.logger.info('Patient selection changed, discarding images for clinical safety');
                        await this.imagingService.discardImages(false);
                    }

                    return selectedIds;
                }),
                catchError((error) => {
                    this.logger.error('Error in imaging safety monitoring:', error);
                    return of(null);
                }),
            ),
        { dispatch: false },
    );

    constructor(private actions$: Actions) {
        console.log('safety effects initialized');
    }
}
